import { FragnoApiValidationError } from "./error.js";

//#region src/api/request-input-context.ts
var RequestInputContext = class RequestInputContext {
	#path;
	#method;
	#pathParams;
	#searchParams;
	#headers;
	#body;
	#parsedBody;
	#inputSchema;
	#shouldValidateInput;
	constructor(config) {
		this.#path = config.path;
		this.#method = config.method;
		this.#pathParams = config.pathParams;
		this.#searchParams = config.searchParams;
		this.#headers = config.headers;
		this.#body = config.rawBody;
		this.#parsedBody = config.parsedBody;
		this.#inputSchema = config.inputSchema;
		this.#shouldValidateInput = config.shouldValidateInput ?? true;
	}
	/**
	* Create a RequestContext from a Request object for server-side handling
	*/
	static async fromRequest(config) {
		return new RequestInputContext({
			method: config.method,
			path: config.path,
			pathParams: config.state.pathParams,
			searchParams: config.state.searchParams,
			headers: config.state.headers,
			parsedBody: config.state.body,
			rawBody: config.rawBody,
			inputSchema: config.inputSchema,
			shouldValidateInput: config.shouldValidateInput
		});
	}
	/**
	* Create a RequestContext for server-side rendering contexts (no Request object)
	*/
	static fromSSRContext(config) {
		return new RequestInputContext({
			method: config.method,
			path: config.path,
			pathParams: config.pathParams,
			searchParams: config.searchParams ?? new URLSearchParams(),
			headers: config.headers ?? new Headers(),
			parsedBody: "body" in config ? config.body : void 0,
			inputSchema: "inputSchema" in config ? config.inputSchema : void 0,
			shouldValidateInput: false
		});
	}
	/**
	* The HTTP method as string (e.g., `GET`, `POST`)
	*/
	get method() {
		return this.#method;
	}
	/**
	* The matched route path (e.g., `/users/:id`)
	* @remarks `string`
	*/
	get path() {
		return this.#path;
	}
	/**
	* Extracted path parameters as object (e.g., `{ id: '123' }`)
	* @remarks `Record<string, string>`
	*/
	get pathParams() {
		return this.#pathParams;
	}
	/**
	* [URLSearchParams](https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams) object for query parameters
	* @remarks `URLSearchParams`
	*/
	get query() {
		return this.#searchParams;
	}
	/**
	* [Headers](https://developer.mozilla.org/en-US/docs/Web/API/Headers) object for request headers
	* @remarks `Headers`
	*/
	get headers() {
		return this.#headers;
	}
	get rawBody() {
		return this.#body;
	}
	/**
	* Access the request body as FormData.
	*
	* Use this method when handling file uploads or multipart form submissions.
	* The request must have been sent with Content-Type: multipart/form-data.
	*
	* @throws Error if the request body is not FormData
	*
	* @example
	* ```typescript
	* defineRoute({
	*   method: "POST",
	*   path: "/upload",
	*   async handler(ctx, res) {
	*     const formData = ctx.formData();
	*     const file = formData.get("file") as File;
	*     const description = formData.get("description") as string;
	*     // ... process file
	*   }
	* });
	* ```
	*/
	formData() {
		if (!(this.#parsedBody instanceof FormData)) throw new Error("Request body is not FormData. Ensure the request was sent with Content-Type: multipart/form-data.");
		return this.#parsedBody;
	}
	/**
	* Check if the request body is FormData.
	*
	* Useful for routes that accept both JSON and FormData payloads.
	*
	* @example
	* ```typescript
	* defineRoute({
	*   method: "POST",
	*   path: "/upload",
	*   async handler(ctx, res) {
	*     if (ctx.isFormData()) {
	*       const formData = ctx.formData();
	*       // handle file upload
	*     } else {
	*       const json = await ctx.input.valid();
	*       // handle JSON payload
	*     }
	*   }
	* });
	* ```
	*/
	isFormData() {
		return this.#parsedBody instanceof FormData;
	}
	/**
	* Input validation context (only if inputSchema is defined)
	* @remarks `InputContext`
	*/
	get input() {
		if (!this.#inputSchema) return;
		return {
			schema: this.#inputSchema,
			valid: async () => {
				if (!this.#shouldValidateInput) return this.#parsedBody;
				return this.#validateInput();
			}
		};
	}
	async #validateInput() {
		if (!this.#inputSchema) throw new Error("No input schema defined for this route");
		if (this.#parsedBody instanceof FormData || this.#parsedBody instanceof Blob) throw new Error("Schema validation is only supported for JSON data, not FormData or Blob");
		const result = await this.#inputSchema["~standard"].validate(this.#parsedBody);
		if (result.issues) throw new FragnoApiValidationError("Validation failed", result.issues);
		return result.value;
	}
};

//#endregion
export { RequestInputContext };
//# sourceMappingURL=request-input-context.js.map